import { useCallback, useEffect, useRef, useState } from 'react';

export const AUTO_REFRESH_INTERVAL_SECONDS = 600; // 10 minutes

/**
 * Shared auto-refresh countdown timer (used by Orders, Order Validation, and Quotes).
 *
 * - Runs a single 1-second interval regardless of rerenders.
 * - Calls the latest `onRefresh` when the countdown hits zero, then resets it.
 * - Skips a refresh if the previous one is still in flight (no overlapping requests).
 * - Clears the interval on unmount.
 */
const useAutoRefreshTimer = (onRefresh, intervalSeconds = AUTO_REFRESH_INTERVAL_SECONDS) => {
    const [timerSeconds, setTimerSeconds] = useState(intervalSeconds);
    const onRefreshRef = useRef(onRefresh);
    const inFlightRef = useRef(false);

    // Keep the latest callback without re-creating the interval.
    onRefreshRef.current = onRefresh;

    const runRefresh = useCallback(async () => {
        if (inFlightRef.current) return;
        inFlightRef.current = true;
        try {
            await onRefreshRef.current();
        } catch (error) {
            console.error('Auto-refresh error:', error);
        } finally {
            inFlightRef.current = false;
        }
    }, []);

    useEffect(() => {
        const intervalId = setInterval(() => {
            setTimerSeconds(prev => {
                if (prev <= 1) {
                    runRefresh();
                    return intervalSeconds;
                }
                return prev - 1;
            });
        }, 1000);
        return () => clearInterval(intervalId);
    }, [intervalSeconds, runRefresh]);

    const restartTimer = useCallback(() => {
        setTimerSeconds(intervalSeconds);
    }, [intervalSeconds]);

    const mm = Math.floor(timerSeconds / 60);
    const ss = timerSeconds % 60;
    const timerFormatted = `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;

    return { timerSeconds, timerFormatted, restartTimer, runRefresh };
};

export default useAutoRefreshTimer;
